import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { MenuPrintScreen } from '@/features/menus/MenuPrintScreen';

type PrintSearch = { cost?: boolean; sell?: boolean; margin?: boolean; cogs?: boolean; pretty?: boolean; from?: 'dialog' };

// The print dialog opens this route as a plain URL in a new tab
// (window.open), so the flags arrive in the query string as `=1` or not at
// all. The router's search parser reads a bare 1 as the number 1, not the
// string '1', so both are accepted (only the string was, and every column
// came out off).
function toBool(v: unknown): boolean {
  return v === '1' || v === 1 || v === true || v === 'true';
}

export const Route = createFileRoute('/_app/menus/$menuId/print')({
  validateSearch: (s: Record<string, unknown>): PrintSearch => ({
    cost: toBool(s.cost),
    sell: toBool(s.sell),
    margin: toBool(s.margin),
    cogs: toBool(s.cogs),
    pretty: toBool(s.pretty),
    // Opened by the builder's print dialog, in a tab of its own.
    ...(s.from === 'dialog' ? { from: 'dialog' as const } : {}),
  }),
  component: PrintRoute,
});

function PrintRoute() {
  const { menuId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const toBuilder = () => void navigate({ to: '/menus/$menuId', params: { menuId } });
  // The dialog's tab closes itself, back to the builder that opened it (a
  // tab opened by script may). Should the browser keep it open, it goes to
  // the builder instead. A bookmarked or reloaded preview goes back as ever.
  const fromDialog = search.from === 'dialog';
  const onBack = () => {
    if (!fromDialog) return toBuilder();
    window.close();
    window.setTimeout(() => {
      if (!window.closed) toBuilder();
    }, 100);
  };
  return (
    <MenuPrintScreen
      menuId={Number(menuId)}
      options={{
        cost: Boolean(search.cost),
        sell: Boolean(search.sell),
        margin: Boolean(search.margin),
        cogs: Boolean(search.cogs),
        pretty: Boolean(search.pretty),
      }}
      onBack={onBack}
      backLabel={fromDialog ? 'Close' : 'Back to the menu'}
    />
  );
}
